import { environment } from '@accredible-frontend-v2/envs';
import { AccredibleUser } from '@accredible-frontend-v2/models';
import {
  AccountsRedirectionKey,
  AccredibleAccountsRedirectionService,
} from '@accredible-frontend-v2/services/accounts-redirection';
import { AccredibleAPIMockData, AccredibleApiService } from '@accredible-frontend-v2/services/api';
import { AccredibleUserApiService } from '@accredible-frontend-v2/services/user';
import {
  GainsightIdentifyPayload,
  identifyGainsightUser,
} from '@accredible-frontend-v2/utils/gainsight-px';
import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, tap } from 'rxjs/operators';
import { EMPTY, Observable, throwError } from 'rxjs';

const API_ACCOUNT_URL = `/v1/accounts`;
const WHOAMI_PATH = '/v1/ler/user/whoami';

@Injectable({
  providedIn: 'root',
})
export class LerAuthenticationApiService extends AccredibleApiService {
  private readonly _document = inject<Document>(DOCUMENT);
  private readonly _userApi = inject(AccredibleUserApiService);
  private readonly _accountsRedirection = inject(AccredibleAccountsRedirectionService);

  getSessionToken(): string {
    return this._getSessionToken();
  }

  checkLocalSession(): void {
    if (this._userApi.isUserSignedIn() && !this.getSessionToken()) {
      this._userApi.deleteUser();
    }
  }

  checkSession(origin = environment.lerUrl): Observable<AccredibleUser> {
    const hostname = new URL(origin).hostname;

    return this._get(`${WHOAMI_PATH}?origin=${hostname}`).pipe(
      map(({ body: { user } }) => {
        this._userApi.setUser(user);
        this._gainsightIdentify(user);
        return user;
      }),
      catchError((err) => {
        this.cookies.delete(AccountsRedirectionKey.SESSION_TOKEN_COOKIE);
        return throwError(() => err);
      }),
    );
  }

  logout(): Observable<boolean> {
    const endpoint = this._userApi.getUser().restrict_to_single_issuer
      ? `${API_ACCOUNT_URL}/sign_out_restricted`
      : `${API_ACCOUNT_URL}/sign_out`;

    return this._delete(endpoint).pipe(
      tap(() => {
        this.cookies.delete(AccountsRedirectionKey.SESSION_TOKEN_COOKIE);
        this.browserStorage.delete(AccountsRedirectionKey.SESSION_TOKEN_COOKIE);
        this._userApi.logout();
      }),
      map(() => false),
      catchError(() => {
        console.error('Failed to logout');
        return throwError(() => false);
      }),
    );
  }

  protected override _handleError(
    res: HttpErrorResponse,
    propagate404 = false,
    mockData?: AccredibleAPIMockData,
  ): Observable<any> {
    switch (res.status) {
      case 401:
        this.cookies.delete(AccountsRedirectionKey.SESSION_TOKEN_COOKIE);
        this._document.location.href = this._accountsRedirection.getLoginUrlOrigin('ler');
        return EMPTY;

      default:
        return super._handleError(res, propagate404, mockData);
    }
  }

  private _gainsightIdentify(user: AccredibleUser): void {
    const { id, name, email, restrict_to_single_issuer } = user;

    identifyGainsightUser(environment.recipientGainsightTagKey, <GainsightIdentifyPayload>{
      user: {
        id,
        firstName: name,
        email: email,
        issuer: restrict_to_single_issuer,
      },
    });
  }
}
